import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Icons } from 'helpers/icons'
import { type IArticle } from 'pages/howTo/types/interfaces/IHowTo'
import colors from 'styles/colors'

import { AccordionItems } from './AccortionItems'

interface IAccordionProps {
    icon?: string
    sectionName: string
    items: IArticle[]
}

export const Accordion = ({ icon, sectionName, items }: IAccordionProps) => {
    const [accordionActive, setAccordionActive] = useState<boolean>(false)
    const accordionRef = useRef<HTMLDivElement>(null)
    const toggleRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()

    const style = {
        height:
            accordionActive && accordionRef.current?.scrollHeight
                ? `calc(${accordionRef.current?.scrollHeight / 10}rem)`
                : toggleRef.current?.scrollHeight
                ? `calc(${toggleRef.current?.scrollHeight / 10}rem)`
                : undefined
    }

    return (
        <StyledAccordion
            $isActive={accordionActive}
            ref={accordionRef}
            style={style}
        >
            <StyledHead
                ref={toggleRef}
                onClick={() => {
                    setAccordionActive(!accordionActive)
                }}
            >
                {icon && (
                    <StyledHeadIcon>
                        <StyledHeadImage src={icon} alt={t('accordionIcon')} />
                    </StyledHeadIcon>
                )}
                <StyledHeadName>{sectionName}</StyledHeadName>
                <StyledHeadArrow>
                    <StyledHeadArrowImage
                        $isActive={accordionActive}
                        src={Icons.chevronRight}
                        alt={t('accordionIcon')}
                    />
                </StyledHeadArrow>
            </StyledHead>
            <StyledBody $isActive={accordionActive}>
                <AccordionItems sectionName={sectionName} items={items} />
            </StyledBody>
        </StyledAccordion>
    )
}

interface StyledAccordionProps {
    $isActive?: boolean
}

const StyledAccordion = styled.div<StyledAccordionProps>`
    background: ${colors.white89deg};
    box-shadow: 0 0.4rem 3rem rgba(0, 0, 0, 0.05);
    border-radius: 2rem;
    margin-bottom: 1rem;
    overflow: hidden;
    transition: 0.2s height;

    ${props =>
        props.$isActive &&
        `
        background-color: ${colors.lightGray};
        overflow-y: auto;
    `}
`

const StyledHead = styled.div`
    align-items: center;
    display: flex;
    height: 7rem;
    padding: 1rem;
`

const StyledHeadIcon = styled.div`
    align-items: center;
    background-color: ${colors.lightGray};
    border-radius: 2rem;
    display: flex;
    justify-content: center;
    height: 5rem;
    width: 5rem;
`

const StyledHeadImage = styled.img`
    height: 3rem;
`

const StyledHeadName = styled.span`
    color: ${colors.blueDark};
    display: block;
    font-size: 1.4rem;
    font-weight: 600;
    flex: 1;
    letter-spacing: 0.02em;
    line-height: 1.7rem;
    margin-left: 1rem;
    margin-right:1.4rem;
`

const StyledHeadArrow = styled.div`
    align-items: center;
    background-color: ${colors.silver};
    border-radius: 1rem;
    display: flex;
    justify-content: center;
    height: 3rem;
    opacity: 0.5;
    width: 3rem;
`

interface StyledHeadArrowImageProps {
    $isActive?: boolean
}

const StyledHeadArrowImage = styled.img<StyledHeadArrowImageProps>`
    transition: 0.4s all;
    transform: rotate(90deg);
    ${props => props.$isActive && `transform: rotate(-90deg)`}
`
interface StyledBodyProps {
    $isActive?: boolean
}

const StyledBody = styled.div<StyledBodyProps>`
    background-color: ${colors.lightGray};

    ${props =>
        !props.$isActive &&
        `
        height: 0;
    `}
`
